// Lists (gap #2, technical design §9 saved views): the office's lists of jobs, invoices, estimates
// and customers, run by the server as each person may see them (views.run). Four starters everyone
// has (open jobs, jobs this week, unpaid invoices, follow-ups), the person's own saved lists and the
// shared ones (views.list). A list's statuses, a search, its sort and columns are changed here
// (views.fields says what may be used) and kept with "Save as a list…" (views.save); one's own, or a
// shared one for those who manage settings, is saved again or deleted (views.delete).
// BL-189: on ListPage, the lists PageHead's tabs; the statuses Segmented, the search ListPage's, the
// columns a ChoiceGroup in a Popover; the rows a DataTable whose heads sort; saving from Field, Checkbox,
// Button and Link, what was done the page's status line under them.

import { useEffect, useMemo, useState } from 'react';
import {
  Button,
  Checkbox,
  Choice,
  ChoiceGroup,
  DataTable,
  Label,
  Link,
  Popover,
  Segmented,
  TextField,
  type DataColumn,
  type MoreRows,
} from '@flowology/ui-web/fl';
import { ListPage, type ListRegion, type PageHeadProps } from '@flowology/ui-web/patterns';
import { PageBanners, usePageCrumb, type NavProps } from '../Nav.tsx';
import { jobHref, openJob } from '../job/job-page.ts';
import { openInvoice } from '../invoice/open-invoice.ts';
import { docHref } from '../shared/doc-link.ts';
import { todayIn } from '../books/model.ts';
import { useAct, useQuery, useToast } from '../books/ui.tsx';
import { useSaveKey } from '../shared/save-key.ts';
import { client } from '../api.ts';
import {
  addViewPage,
  cellText,
  picked,
  sameSpec,
  searched,
  sortedBy,
  starters,
  toggledColumn,
  viewInput,
  VIEW_PAGE,
  withPicked,
  withSearch,
  type EntityInfo,
  type SavedView,
  type ViewResult,
  type ViewSpec,
} from './lists-model.ts';
import './lists.css';

type Chosen = { kind: 'starter'; key: string } | { kind: 'saved'; id: string };

/** Who may share a list with everyone: whoever holds settings.edit, as views.save asks (the owner and managers). */
const SHARE_PERMS = ['settings.edit'];

const LEDE = 'Open jobs, this week, what’s owed and what to follow up. Change one and save it as your own.';

export function ListsScreen({ nav }: { nav: NavProps }) {
  const timeZone = nav.timeZone ?? Intl.DateTimeFormat().resolvedOptions().timeZone;
  const today = todayIn(timeZone);
  const [said, say] = useToast();
  const fields = useQuery<{ entities: EntityInfo[] }>('views.fields', {});
  const saved = useQuery<{ views: SavedView[] }>('views.list', {});
  const entities = useMemo(
    () => new Map((fields.data?.entities ?? []).map((e) => [e.entity, e])),
    [fields.data],
  );
  // A starter shows only to those who may list its entity (views.fields leaves the others out).
  const mineStarters = useMemo(
    () => starters(today, timeZone).filter((s) => entities.has(s.spec.entity)),
    [today, timeZone, entities],
  );
  const [chosen, setChosen] = useState<Chosen>({ kind: 'starter', key: 'open-jobs' });
  const savedView = chosen.kind === 'saved' ? saved.data?.views.find((v) => v.id === chosen.id) : undefined;
  const base: ViewSpec | null =
    chosen.kind === 'starter'
      ? (mineStarters.find((s) => s.key === chosen.key)?.spec ?? mineStarters[0]?.spec ?? null)
      : savedView
        ? (savedView.spec as unknown as ViewSpec)
        : null;
  const name =
    chosen.kind === 'starter'
      ? (mineStarters.find((s) => s.key === chosen.key)?.name ?? 'Lists')
      : (savedView?.name ?? 'Lists');
  const mine = (saved.data?.views ?? []).filter((v) => v.mine);
  const shared = (saved.data?.views ?? []).filter((v) => !v.mine);
  const crumb = usePageCrumb(nav);
  // the lists, one a tab: the starters, then your own and the shared ones, each group named
  const tab = (c: Chosen, label: string, group?: string) => ({
    label,
    ...(group ? { group } : {}),
    isCurrent:
      c.kind === 'starter'
        ? chosen.kind === 'starter' && chosen.key === c.key
        : chosen.kind === 'saved' && chosen.id === c.id,
    onPress: () => setChosen(c),
  });
  const head: PageHeadProps = {
    ...(crumb ? { crumb } : {}),
    title: name,
    lede: LEDE,
    tabs: {
      label: 'Lists',
      items: [
        ...mineStarters.map((s) => tab({ kind: 'starter', key: s.key }, s.name)),
        ...mine.map((v) => tab({ kind: 'saved', id: v.id }, v.name, 'Your lists')),
        ...shared.map((v) => tab({ kind: 'saved', id: v.id }, v.name, 'Shared')),
      ],
    },
  };
  const entity = base ? entities.get(base.entity) : undefined;
  return (
    <div className="app">
      <main>
        <PageBanners nav={nav} />
        {!fields.data || !base || !entity ? (
          <ListPage
            head={head}
            list={{
              title: 'The list',
              what: 'the lists',
              status: fields.error || saved.error ? 'failed' : 'loading',
              failedText: fields.error ?? saved.error ?? 'The lists didn’t load.',
              onRetry: () => {
                void fields.reload();
                void saved.reload();
              },
            }}
          />
        ) : (
          <ListPanel
            key={chosen.kind === 'starter' ? chosen.key : chosen.id}
            head={head}
            name={name}
            base={base}
            entity={entity}
            saved={savedView}
            canShare={SHARE_PERMS.some((p) => (nav.permissions ?? []).includes(p))}
            timeZone={timeZone}
            said={said}
            say={say}
            onSaved={async (v) => {
              await saved.reload();
              if (v) setChosen({ kind: 'saved', id: v.id });
              else setChosen({ kind: 'starter', key: 'open-jobs' });
            }}
          />
        )}
      </main>
    </div>
  );
}

type Row = { id: string; values: Record<string, unknown> };

/** Where a row's number opens it: a job's page, an invoice's sheet; the other lists' rows don't open. */
const OPENS: Record<string, { href: (id: string) => string; open: (id: string) => void }> = {
  jobs: { href: jobHref, open: (id) => openJob(id) },
  invoices: { href: (id) => docHref({ kind: 'invoice', id }), open: openInvoice },
};

function ListPanel({
  head,
  name,
  base,
  entity,
  saved,
  canShare,
  timeZone,
  said,
  say,
  onSaved,
}: {
  head: PageHeadProps;
  name: string;
  base: ViewSpec;
  entity: EntityInfo;
  saved: SavedView | undefined;
  canShare: boolean;
  timeZone: string;
  said: string | null;
  say: (m: string) => void;
  onSaved: (v: SavedView | null) => Promise<void>;
}) {
  // the list as changed here; it starts from its own spec (a new list remounts this panel)
  const [spec, onChange] = useState<ViewSpec>(base);
  const first = useQuery<ViewResult>('views.run', viewInput(spec));
  // the pages "Show more" read on, under the first (list caps L1); a list changed starts again
  const specKey = JSON.stringify(spec);
  const [later, setLater] = useState<{ key: string; of: ViewResult; result: ViewResult } | null>(null);
  const [moreBusy, setMoreBusy] = useState(false);
  const [moreError, setMoreError] = useState<string | null>(null);
  const shownResult =
    first.data && later?.key === specKey && later.of === first.data ? later.result : first.data;
  const readMore = () => {
    const had = shownResult;
    const of = first.data;
    if (!had?.truncated || !of || moreBusy) return;
    const at = specKey;
    setMoreBusy(true);
    setMoreError(null);
    client
      .call<ViewResult>('views.run', viewInput(spec, had.rows.length))
      .then((page) => setLater({ key: at, of, result: addViewPage(had, page) }))
      .catch(() => setMoreError('Couldn’t read the rest of the list. Try again.'))
      .finally(() => setMoreBusy(false));
  };
  const act = useAct(say, () => undefined);
  const [saving, setSaving] = useState<null | { name: string; shared: boolean }>(null);
  const [deleting, setDeleting] = useState(false);
  const saveKey = useSaveKey(JSON.stringify([saving, spec]));
  const changed = !sameSpec(spec, base);
  const enums = entity.fields.filter((f) => f.type === 'enum' && f.values?.length);
  const searchable = entity.fields.find((f) => f.field === 'customer' || f.field === 'name');
  // the search applies once the typing stops, as the other lists' searches do
  const searchField = searchable?.field;
  const [typed, setTyped] = useState(searchField ? searched(base, searchField) : '');
  useEffect(() => {
    if (!searchField) return;
    const t = setTimeout(
      () =>
        onChange((s) => (searched(s, searchField) === typed.trim() ? s : withSearch(s, searchField, typed))),
      300,
    );
    return () => clearTimeout(t);
  }, [typed, searchField]);
  const shown = shownResult?.columns.map((c) => c.field) ?? spec.columns ?? [];
  const sort = spec.sort?.[0];
  const save = async (asNew: boolean) => {
    const r = await act.run<SavedView>(
      'views.save',
      {
        ...(asNew || !saved ? {} : { id: saved.id }),
        name: asNew ? saving!.name : saved!.name,
        spec,
        shared: asNew ? saving!.shared : saved!.shared,
      },
      asNew ? `Saved “${saving!.name}”. It’s under Your lists.` : `Saved “${saved!.name}”.`,
      { idempotencyKey: saveKey.key },
    );
    if (r) {
      saveKey.renew();
      setSaving(null);
      await onSaved(r);
    }
  };
  const undo = () => {
    onChange(base);
    setTyped(searchField ? searched(base, searchField) : '');
  };

  // a job's number opens its page, an invoice's its sheet, while the number is shown
  const opens = OPENS[entity.entity] && shown.includes('number') ? OPENS[entity.entity] : undefined;
  const columns: DataColumn<Row>[] = (shownResult?.columns ?? []).map((c) => ({
    id: c.field,
    label: c.label,
    ...(c.type === 'money' ? { align: 'end' as const } : {}),
    ...(opens && c.field === 'number' ? { way: true } : {}),
    cell: (r) => ({ text: cellText(entity.entity, c, r.values[c.field], timeZone) }),
  }));
  const rows: Row[] = (shownResult?.rows ?? []).map((r) => ({ id: String(r['id']), values: r }));
  const total = shownResult ? (shownResult.total ?? shownResult.rows.length) : 0;
  const more: MoreRows | null = shownResult?.truncated
    ? {
        label: `Show ${Math.min(VIEW_PAGE, Math.max(total - rows.length, 1))} more of ${total}`,
        onPress: readMore,
        isBusy: moreBusy,
        error: moreError,
      }
    : null;

  const list: ListRegion = {
    title: 'The list',
    what: 'the list',
    status: !shownResult ? (first.error ? 'failed' : 'loading') : rows.length ? 'ready' : 'empty',
    emptyText: 'Nothing on this list right now.',
    failedText: first.error ?? 'The list didn’t load.',
    onRetry: () => void first.reload(),
    children: (
      <DataTable
        columns={columns}
        rows={rows}
        {...(opens ? { hrefOf: (r: Row) => opens.href(r.id), onOpen: (r: Row) => opens.open(r.id) } : {})}
        more={more}
        empty="Nothing on this list right now."
        sort={sort ? { column: sort.field, dir: sort.dir } : null}
        onSort={(field) => onChange(sortedBy(spec, field))}
      />
    ),
    after: (
      <div className="lists__save">
        {saving ? (
          <div className="lists__form">
            <TextField
              label="Name"
              value={saving.name}
              autoFocus
              onChange={(v) => setSaving({ ...saving, name: v })}
            />
            {canShare && (
              <Checkbox isSelected={saving.shared} onChange={(on) => setSaving({ ...saving, shared: on })}>
                Share with everyone
              </Checkbox>
            )}
            <div className="lists__acts">
              <Button
                variant="primary"
                isBusy={act.busy}
                isDisabled={!saving.name.trim()}
                onPress={() => void save(true)}
              >
                Save the list
              </Button>
              <Link onPress={() => setSaving(null)}>Cancel</Link>
            </div>
          </div>
        ) : deleting && saved ? (
          <div className="lists__acts">
            <span>
              Delete “{saved.name}”{saved.shared ? ' for everyone' : ''}?
            </span>
            <Button
              variant="danger"
              isBusy={act.busy}
              onPress={() =>
                void act
                  .run('views.delete', { id: saved.id }, `Deleted “${saved.name}”.`)
                  .then((r) => (r ? onSaved(null) : undefined))
              }
            >
              Delete
            </Button>
            <Link onPress={() => setDeleting(false)}>Keep it</Link>
          </div>
        ) : (
          <div className="lists__acts">
            {saved?.canEdit && changed && (
              <Button variant="primary" isBusy={act.busy} onPress={() => void save(false)}>
                Save changes
              </Button>
            )}
            {(changed || !saved) && (
              <Button
                variant={saved?.canEdit && changed ? 'secondary' : 'primary'}
                onPress={() => setSaving({ name: saved ? `${saved.name} (copy)` : name, shared: false })}
              >
                Save as a list…
              </Button>
            )}
            {changed && <Link onPress={undo}>Undo changes</Link>}
            {saved?.canEdit && <Link onPress={() => setDeleting(true)}>Delete list</Link>}
          </div>
        )}
        <p className="lists__said" role="status">
          {said}
        </p>
      </div>
    ),
  };

  return (
    <ListPage
      head={head}
      {...(saved?.shared
        ? {
            notice: (
              <p className="lists__shared">
                <Label>Shared with everyone</Label>
              </p>
            ),
          }
        : {})}
      {...(searchable
        ? {
            search: {
              label: `${searchable.label} contains`,
              value: typed,
              onChange: setTyped,
              maxLength: 100,
            },
          }
        : {})}
      filters={
        <>
          {enums.map((f) => (
            <div key={f.field} className="lists__pick">
              <span className="lists__name" aria-hidden="true">
                {f.label}
              </span>
              <div className="lists__segs">
                <Segmented
                  selectionMode="multiple"
                  label={f.label}
                  items={f.values!.map((v) => ({
                    id: v,
                    label: cellText(entity.entity, { field: f.field, type: 'enum' }, v, timeZone),
                  }))}
                  selectedKeys={picked(spec, f.field) ?? f.values!}
                  onSelectionChange={(ids) =>
                    onChange(
                      withPicked(
                        spec,
                        f.field,
                        f.values!.filter((v) => ids.includes(v)),
                        f.values!,
                      ),
                    )
                  }
                />
              </div>
            </div>
          ))}
          <Popover label="Columns" trigger={<Button>Columns</Button>}>
            <ChoiceGroup
              label="Columns"
              value={shown}
              onChange={(next) => {
                // the one ticked or unticked; at least one stays
                const f = next.find((x) => !shown.includes(x)) ?? shown.find((x) => !next.includes(x));
                if (f) onChange(toggledColumn(spec, f, shown));
              }}
            >
              {entity.fields.map((f) => (
                <Choice key={f.field} value={f.field}>
                  {f.label}
                </Choice>
              ))}
            </ChoiceGroup>
          </Popover>
        </>
      }
      list={list}
    />
  );
}
